import FaviconHead from '@components/head/favicons';
import HreflangHead from '@components/head/hreflangHead';
import OnboardingMetaTagHead from '@components/head/onboardingMetadata';
import {
    COMMON,
    CONSENTS,
    FORM,
    ONBOARDING,
    SECONDS_PER_DAY,
    STORY,
} from '@constants';
import { EventTriggerSource } from '@enums';
import {
    GetOnboardingByCompanyAndProductDocument,
    GetOnboardingByCompanyAndProductQuery,
    OnboardingFragment,
} from '@graphql/generated/graphql';
import { useOnboarding } from '@hooks';
import { Flow } from '@lib/pimster-flow/components/Flow';
import { getCompanyConfig } from '@lib/uikit';
import { PageCommonProps } from '@types';
import { buildHreflangArray } from '@utils/hreflang';
import {
    withApolloClient,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodsPlugins';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface Props extends PageCommonProps {
    onboarding: OnboardingFragment;
}

const OnboardingPage: NextPage<Props> = ({
    onboarding,
    hrefLang,
    companyMeta,
    productMeta,
}: Props) => {
    const { setOnboardingHasBeenSeen } = useOnboarding({
        productSlug: productMeta.Slug!,
    });
    return (
        <>
            <OnboardingMetaTagHead onboarding={onboarding} />
            <FaviconHead productMeta={productMeta} />
            <HreflangHead
                companySlug={companyMeta.Slug!}
                defaultLocale={companyMeta.DefaultLocale!}
                refs={hrefLang}
            />
            <Flow
                module={onboarding}
                onExit={setOnboardingHasBeenSeen}
                triggerSource={EventTriggerSource.ONBOARDING}
            />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps<Props> = withErrorsGSP<Props>(
    withApolloClient<Props>(
        withCompany<Props>(
            withProduct<Props>(
                async ({ apolloClient, company, product, locale }) => {
                    const {
                        data: { products },
                    } =
                        await apolloClient.query<GetOnboardingByCompanyAndProductQuery>(
                            {
                                query: GetOnboardingByCompanyAndProductDocument,
                                variables: {
                                    companySlug: company.Slug,
                                    productSlug: product.Slug,
                                    locale,
                                },
                            }
                        );

                    const onboarding = products?.[0].Onboarding;

                    if (!onboarding) return { notFound: true };

                    return {
                        props: {
                            onboarding,
                            hrefLang: buildHreflangArray({
                                entity: onboarding,
                                currentLocale: locale!,
                                basePath: `/${product.Slug}/onboarding`,
                            }),
                            ...(await serverSideTranslations(locale!, [
                                COMMON,
                                CONSENTS,
                                FORM,
                                ONBOARDING,
                                STORY,
                            ])),
                            ...(await getCompanyConfig(
                                apolloClient,
                                company.Slug!,
                                locale!
                            )),
                        },
                        revalidate: SECONDS_PER_DAY,
                    };
                }
            )
        )
    )
);

export default OnboardingPage;
